/**
 * Arama reklamları API'sinin MERKEZİ İŞLEM BÜTÇESİ sözleşmesi (v3.3; v3.4'te tek paydalı modele indirildi). Belge:
 * docs/spec/castinflow/google-ads-connect.md § 18.
 *
 * Sağlayıcı, reklam API'sine yapılan işlemleri uygulamanın bulut projesi başına sayar; sınır BÜTÜN çalışma alanları için
 * ortaktır. Bu yüzden bütçe platform düzeyindedir ve çalışma alanları arasında adil paylaştırılır.
 *
 * MODEL (v3.4 — tek kök payda `B`; hiçbir yüzde başka bir yüzdenin ya da iki sınıfın toplamının üstüne uygulanmaz):
 *
 *   B   = GOOGLE_ADS_DAILY_OPERATION_BUDGET          kayan 24 saatte platformun tamamı (tanımsızsa gerçek çağrı YOK)
 *   R   = B − ⌊B × (100 − r) / 100⌋                  bağlama havuzu (r = bağlama rezervi %; yalnız `connect` kullanır)
 *   S   = B − R                                      eşitleme havuzu = eşitleme tavanı (yalnız `sync` kullanır)
 *   T_s = max(1, ⌊S × t / 100⌋),  T_c = max(1, ⌊R × t / 100⌋)
 *                                                    çalışma alanı tavanı SINIF BAŞINA; payda o sınıfın havuzudur
 *   P   = ⌈S × 50 / 100⌉                             sayaç kaydı yokken eşitleme havuzuna yazılan ön yük
 *
 * İki havuz B'yi BÖLER (R + S = B, ödünç alma yok): kabul edilen bağlama ≤ R, kabul edilen eşitleme + ön yük ≤ S, dolayısıyla
 * herhangi bir sınıf / çalışma alanı karışımında kabul edilen toplam ≤ B (sayaçta ayrıca toplam denetimi de vardır). r ve t
 * 1–99 arasındadır ve her havuz en az 2 işlemdir → T_s < S ve T_c < R: tek çalışma alanı ne eşitleme tavanını ne bağlama
 * rezervini tek başına tüketebilir.
 *
 * - Bütçe değeri ÜRÜN SABİTİ DEĞİLDİR: yapılandırmadan gelir. Erişim seviyesinden varsayılan türetilmez; tanımsızsa gerçek
 *   çağrı YAPILMAZ (en temkinli davranış) ve süreç açık uyarı verir.
 * - "Gün" sağlayıcının tanımıyla KAYAN 24 SAATTİR: pencere dakikalık dilimlerle sayılır ve en az 24 saati kapsar.
 * - Bütçe dolduğunda iş HATA SAYILMAZ: "bekliyor" durumuna geçer ve yer açılacağı an bildirilir.
 *
 * Paylar ve bekleme süreleri Castinflow'un KENDİ politikasıdır (sağlayıcı değeri değildir); paylar yapılandırılabilir.
 */
import { z } from 'zod';
/** Öncelik sınıfları: `connect` etkileşimli (hesap bağlama), `sync` arka plan (harcama eşitlemesi) */
export const GOOGLE_ADS_BUDGET_CLASSES = ['connect', 'sync'];
/**
 * Çağrıya izin verilmeme nedeni (kapalı küme):
 *   unconfigured       bütçe tanımlı değil — gerçek çağrı yapılmaz (yapılandırma eksiği; zamanla açılmaz)
 *   store_unavailable  sayaç deposuna ulaşılamıyor — sayılamayan çağrı yapılmaz
 *   provider_cooldown  sağlayıcı kota / hız yanıtı verdi; kontrollü bekleme sürüyor
 *   minute_rate        dakikalık tavan doldu
 *   exhausted          platform bütçesi (kayan 24 saat) doldu — havuzlar B'yi böldüğü için olağan durumda havuz reddinden
 *                      önce oluşmaz; savunma amaçlı son denetimdir
 *   class_share        isteğin sınıfının havuzu doldu (eşitleme havuzu S ya da bağlama havuzu R)
 *   tenant_share       çalışma alanının o sınıftaki tavanı doldu
 */
export const GOOGLE_ADS_BUDGET_DENIALS = [
    'unconfigured',
    'store_unavailable',
    'provider_cooldown',
    'minute_rate',
    'exhausted',
    'class_share',
    'tenant_share',
];
/** KAPASİTE nedenleri: zamanla kendiliğinden açılır → iş "bekliyor" olur (hata değildir) */
export const GOOGLE_ADS_BUDGET_WAITS = [
    'provider_cooldown',
    'minute_rate',
    'exhausted',
    'class_share',
    'tenant_share',
];
export function googleAdsBudgetIsWait(d) {
    return GOOGLE_ADS_BUDGET_WAITS.includes(d);
}
/** Castinflow'un bütçe politikası sabitleri (sağlayıcı değeri DEĞİL) */
export const GOOGLE_ADS_BUDGET = {
    /** Sağlayıcının "gün" tanımı: kayan 24 saat */
    windowHours: 24,
    /** Sayaç dilimi (saniye): pencere bu dilimlerle sayılır ve en az 24 saati kapsar */
    bucketSeconds: 60,
    /**
     * Bağlama rezervi `r` (%; B'nin payı): yalnız hesap bağlama keşfi kullanır, eşitleme giremez. Eşitleme tavanı = B − rezerv.
     * Gerekçe: bir keşif en çok 1 + 20 + 20 okuma yapar (GOOGLE_ADS_CONNECT_LIMITS), eşitleme hesap başına turda 1 okuma; bağlama
     * seyrek ama etkileşimli olduğu için bütçenin üçte birine yakın kısmı ayrılır.
     */
    connectReservePercentDefault: 30,
    /**
     * Çalışma alanı tavanı `t` (%): payda isteğin SINIFININ HAVUZUDUR (eşitlemede S, bağlamada R); iki sınıf ayrı sayılır.
     * Gerekçe: 40 ile bir havuzu tüketmek için en az üç çalışma alanı gerekir; tek çalışma alanının en kötü keşfi de sığar.
     */
    tenantPoolPercentDefault: 40,
    /** Her havuzun en az bu kadar işlem olması gerekir: çalışma alanı tavanı havuzdan KÜÇÜK kalabilsin */
    minPoolOperations: 2,
    /**
     * Sayaç kaydı yoksa (ilk başlangıç ya da depo kaybı) son 24 saatin kullanımı BİLİNMEZ: eşitleme payının bu kadarı kullanılmış
     * sayılır (ön yük, 24 saatte kendiliğinden düşer). Bağlama payı etkilenmez.
     */
    lossPrechargePercent: 50,
    /** Hesap üst verisi (para birimi, saat dilimi, ad) kısa ömürlü önbelleğin ömrü (gün): bu sürede yeniden okunmaz */
    metadataTtlDays: 7,
    /** Sayaç deposuna ulaşılamadığında yeniden deneme önerisi (saniye) */
    storeRetrySeconds: 300,
    /** Sağlayıcı GÜNLÜK kota yanıtında bekleme: ilk süre ve üst sınır (saniye); her yinelenen yanıtta ikiye katlanır */
    dailyBackoffBaseSeconds: 900,
    dailyBackoffMaxSeconds: 6 * 3600,
    /** Sağlayıcı KISA SÜRELİ hız yanıtında bekleme: ilk süre ve üst sınır (saniye) */
    shortBackoffBaseSeconds: 5,
    shortBackoffMaxSeconds: 300,
    /** Sağlayıcının bildirdiği bekleme süresinin kabul edilen üst sınırı (saniye) */
    hintMaxSeconds: 24 * 3600,
};
const isPercent = (v) => Number.isInteger(v) && v >= 1 && v <= 99;
const isCount = (v) => v === null || (Number.isInteger(v) && v >= 1);
/** Sınıfın havuzu ve çalışma alanının o havuzdaki tavanı (bütçe varken her çalışma alanı en az bir işlem yapabilir) */
const tenantCapOf = (pool, pct) => Math.max(1, Math.floor((pool * pct) / 100));
/**
 * Politikanın sorunu (Türkçe, değer yankılamaz) ya da `null`. Süreç başlangıcında (yapılandırma) ve bütçe kurulurken aynı
 * kural uygulanır: paylar 1–99 tam sayı; sayılar pozitif tam sayı; bütçe tanımlıysa iki havuzun her biri en az
 * `minPoolOperations` işlem (aksi hâlde tek çalışma alanı bir havuzu tek başına tüketebilirdi).
 */
export function googleAdsBudgetPolicyProblem(p) {
    if (!isPercent(p.connectReservePercent) || !isPercent(p.tenantPoolPercent))
        return 'arama reklamları bütçesi payı 1–99 arasında tam sayı olmalı';
    if (!isCount(p.dailyOperations) || !isCount(p.perMinute))
        return 'arama reklamları bütçesi pozitif tam sayı olmalı';
    if (p.dailyOperations !== null) {
        const sync = Math.floor((p.dailyOperations * (100 - p.connectReservePercent)) / 100);
        const reserve = p.dailyOperations - sync;
        if (Math.min(sync, reserve) < GOOGLE_ADS_BUDGET.minPoolOperations)
            return `arama reklamları bütçesi çok küçük: eşitleme ve bağlama havuzlarının her biri en az ${String(GOOGLE_ADS_BUDGET.minPoolOperations)} işlem olmalı`;
    }
    return null;
}
/** Politikadan türetilen sınırlar; bütçe tanımsızsa `null`. Geçersiz politika için çağıran önce `…PolicyProblem` denetler. */
export function googleAdsBudgetLimits(p) {
    if (p.dailyOperations === null)
        return null;
    const total = Math.max(0, Math.floor(p.dailyOperations));
    // Eşitleme havuzu aşağı yuvarlanır: yuvarlama farkı bağlama rezervinde kalır (R = B − S)
    const sync = Math.floor((total * (100 - p.connectReservePercent)) / 100);
    const connectReserve = total - sync;
    return {
        total,
        sync,
        connectReserve,
        tenantSync: sync === 0 ? 0 : tenantCapOf(sync, p.tenantPoolPercent),
        tenantConnect: connectReserve === 0 ? 0 : tenantCapOf(connectReserve, p.tenantPoolPercent),
        minute: p.perMinute,
        lossPrecharge: Math.ceil((sync * GOOGLE_ADS_BUDGET.lossPrechargePercent) / 100),
    };
}
/**
 * v3.4 — `B`'nin sağlayıcı sınırının ALTINDA olduğunun yapılandırma denetimi. Sahip iki değeri BEYAN eder (çağrıyla
 * öğrenilemez; erişim seviyesi beyanıyla aynı düzen):
 *   providerDailyLimit  sağlayıcının bu bulut projesine (erişim seviyesine) tanıdığı günlük işlem sınırı
 *   otherDailyUse       aynı projeyi / kimliği kullanan BAŞKA tüketicilere (ör. iç araçlar) ayrılan günlük işlem
 * Kural: B tanımlıysa sınır beyan edilmiş olmalı ve B + otherDailyUse ≤ providerDailyLimit. Castinflow diğer tüketicinin
 * kullanımını göremez; bu yüzden onların payı ayrılmadan B sağlayıcı sınırına eşitlenemez. Sorun yoksa `null`.
 */
export function googleAdsBudgetHeadroomProblem(x) {
    if (x.budget === null)
        return null;
    if (x.providerDailyLimit === null)
        return 'GOOGLE_ADS_DAILY_OPERATION_BUDGET verildiğinde GOOGLE_ADS_PROVIDER_DAILY_LIMIT (sağlayıcının bu projeye tanıdığı günlük sınır) da beyan edilmeli';
    if (x.otherDailyUse >= x.providerDailyLimit)
        return 'GOOGLE_ADS_PROVIDER_OTHER_DAILY_USE sağlayıcı sınırından küçük olmalı';
    if (x.budget + x.otherDailyUse > x.providerDailyLimit)
        return 'GOOGLE_ADS_DAILY_OPERATION_BUDGET + GOOGLE_ADS_PROVIDER_OTHER_DAILY_USE sağlayıcı sınırını (GOOGLE_ADS_PROVIDER_DAILY_LIMIT) aşamaz';
    return null;
}
/** Sınıfın havuzu (`S` ya da `R`) */
export function googleAdsBudgetPoolOf(l, klass) {
    return klass === 'sync' ? l.sync : l.connectReserve;
}
/** Çalışma alanının sınıftaki tavanı (`T_s` ya da `T_c`) */
export function googleAdsBudgetTenantCapOf(l, klass) {
    return klass === 'sync' ? l.tenantSync : l.tenantConnect;
}
/**
 * Bağlama bütçesinin çalışma alanına gösterilen durumu (`GET /settings/provider-connections` → `googleAdsConnect.budget`):
 *   open          yer var
 *   waiting       yer yok; `retryAt` anında açılması bekleniyor (hata değil)
 *   unconfigured  bu ortamda bütçe tanımlı değil (kullanıcı düzeltemez)
 *   unavailable   sayaç deposuna ulaşılamıyor (geçici)
 * Sayı, kalan miktar ya da başka çalışma alanına ait bilgi TAŞIMAZ.
 */
export const GOOGLE_ADS_BUDGET_STATES = ['open', 'waiting', 'unconfigured', 'unavailable'];
export const googleAdsBudgetStatusSchema = z.object({
    state: z.enum(GOOGLE_ADS_BUDGET_STATES),
    /** Yer açılmasının beklendiği an (UTC, ISO 8601); yalnız `waiting` / `unavailable` iken dolu olabilir */
    retryAt: z.string().nullable(),
});
export function googleAdsBudgetStateOf(denial) {
    if (denial === null)
        return 'open';
    if (denial === 'unconfigured')
        return 'unconfigured';
    if (denial === 'store_unavailable')
        return 'unavailable';
    return 'waiting';
}
/** Bağlama akışının bütçe reddinde `error.details` (neden + beklenen açılma anı) */
export const googleAdsBudgetDetailsSchema = z.object({
    reason: z.enum(['provider_budget_exhausted', 'provider_budget_unconfigured']),
    retryAt: z.string().nullable().optional(),
});
/** Harcama eşitlemesinin "okuma hakkı bekleniyor" kodu (`ad_accounts.last_error_code`; hata DEĞİL, bekleme) */
export const AD_SYNC_BUDGET_WAIT_CODE = 'provider_budget';
//# sourceMappingURL=google-ads-budget.js.map